import { Button, type ButtonProps, IconButton } from '@chakra-ui/react'
import { Check, Copy, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { copyToClipboard } from '../utils/copyToClipboard'

export type CopyButtonProps = {
  /**
   * The text written to the clipboard on click. Pass a function when the
   * payload must be stamped at click time (e.g. a diagnostics snapshot).
   */
  value: string | (() => string)
  /**
   * What is being copied, for the accessible label of the icon-only button —
   * e.g. "Agent ID" yields "Copy Agent ID" / "Agent ID copied". Defaults to
   * "value". Ignored in text mode (the visible text is the accessible name)
   * unless `copyAriaLabel` / `copiedAriaLabel` override it.
   */
  label?: string
  /** lucide icon size in px. Defaults to 14. */
  iconSize?: number
  /** lucide stroke width. Defaults to the icon's own default. */
  iconStrokeWidth?: number
  /**
   * Render a full text+icon button with this visible text instead of the
   * icon-only default (e.g. "Copy Authorization Link"). The leading icon still
   * flips to a check on success.
   */
  text?: string
  /** Visible text after a successful copy, in text mode. Defaults to "Copied". */
  copiedText?: string
  /**
   * Accessible name while idle, in icon-only mode. Overrides `Copy ${label}`
   * so hosts can pass a translated phrase.
   */
  copyAriaLabel?: string
  /**
   * Accessible name after a successful copy, in icon-only mode. Overrides
   * `${label} copied` so hosts can pass a translated phrase.
   */
  copiedAriaLabel?: string
  /** Called after the clipboard write succeeds — e.g. to toast confirmation. */
  onCopied?: () => void
  /** Called with the error if the clipboard write throws — e.g. to toast a failure. */
  onCopyError?: (error: unknown) => void
  // Both IconButton and Button accept the same Chakra style props; typing the
  // passthrough as ButtonProps covers every call site (size/variant/opacity/
  // colorPalette/px/…) for both render modes.
} & Omit<ButtonProps, 'aria-label' | 'onClick' | 'children' | 'value'>

/**
 * A button that copies `value` to the clipboard and flips to a check for ~1.5s
 * as confirmation. Wraps the shared `copyToClipboard` (Electron + web) and the
 * copied-state/icon toggle that every copy affordance was otherwise
 * reimplementing.
 *
 * Icon-only by default; pass `text` to render a full text+icon button. Fails
 * closed — a failed copy never shows the "copied" confirmation, and the icon
 * flips to an X for ~1.5s. Use `onCopied` / `onCopyError` to surface a toast.
 * Styling and visibility (size, variant, hover-reveal opacity, etc.) are the
 * caller's via passthrough props.
 */
export const CopyButton = ({
  value,
  label = 'value',
  iconSize = 14,
  iconStrokeWidth,
  text,
  copiedText = 'Copied',
  copyAriaLabel,
  copiedAriaLabel,
  onCopied,
  onCopyError,
  type = 'button',
  ...rest
}: CopyButtonProps) => {
  const [copied, setCopied] = useState(false)
  const [failed, setFailed] = useState(false)
  const revertTimer = useRef<ReturnType<typeof setTimeout>>(undefined)

  useEffect(() => () => clearTimeout(revertTimer.current), [])

  const handleCopy = async () => {
    try {
      const textToCopy = typeof value === 'function' ? value() : value
      await copyToClipboard(textToCopy)
      setFailed(false)
      setCopied(true)
      clearTimeout(revertTimer.current)
      revertTimer.current = setTimeout(() => setCopied(false), 1500)
      onCopied?.()
    } catch (error) {
      // Leave `copied` false rather than claim a success that did not happen —
      // the value stays on screen for a manual copy. The X is the visible
      // failure, matching the previous per-site copy buttons.
      setCopied(false)
      setFailed(true)
      clearTimeout(revertTimer.current)
      revertTimer.current = setTimeout(() => setFailed(false), 1500)
      onCopyError?.(error)
    }
  }

  const icon = copied ? (
    <Check size={iconSize} strokeWidth={iconStrokeWidth} />
  ) : failed ? (
    <X size={iconSize} strokeWidth={iconStrokeWidth} />
  ) : (
    <Copy size={iconSize} strokeWidth={iconStrokeWidth} />
  )

  if (text !== undefined) {
    const shown = copied ? copiedText : text
    // The visible text is the button's accessible name, so no aria-label.
    return (
      <Button type={type} onClick={handleCopy} {...rest}>
        {icon}
        {shown}
      </Button>
    )
  }

  return (
    <IconButton
      type={type}
      aria-label={
        copied
          ? (copiedAriaLabel ?? `${label} copied`)
          : (copyAriaLabel ?? `Copy ${label}`)
      }
      onClick={handleCopy}
      {...rest}
    >
      {icon}
    </IconButton>
  )
}
